import type { AnalyticsPlacementTypes } from '@core/analytics'
import { createStoreChunk } from '@core/store'

// Audio subject for a deferred download (structural mirror of `@features/audio` `TDownloadAudio` —
// kept local so `features/auth` doesn't depend on `features/audio`; the resolver lives in the app
// layer, which may cross feature boundaries).
export type TDownloadIntentAudio = {
    id: string
    title: string
    authorUsername: string
    authorId: string
    avatarUrl: string
}

// Same reasoning as the audio above — a structural mirror of `@features/collection`'s
// `TDownloadCollection`.
export type TDownloadIntentCollection = {
    id: string
    title: string
}

/**
 * The action a guest intended before auth, replayed on success (follow / like / download). audio-web
 * persisted this in `sessionStorage` and replayed via `window` CustomEvents; here it's a store chunk
 * read by the app-level `AuthIntentRunner`. Persisted to localStorage (through the store engine — the
 * storage rule keeps raw sessionStorage out of `features`) so it survives the full-page OAuth
 * redirect round-trip; `setAt` bounds staleness via `INTENT_TTL_MS` in the runner. Payloads are
 * serializable only.
 */
export type TAuthIntent =
    | { kind: 'follow'; username: string }
    | { kind: 'like'; audioId: string; placement: AnalyticsPlacementTypes }
    | { kind: 'download'; audio: TDownloadIntentAudio; placement: string }
    | { kind: 'download-collection'; collection: TDownloadIntentCollection }

/**
 * Armed when the auth modal opens and disarmed by a plain sign-in, so what survives to the app-level
 * runner means "this auth ended in a registration". Persisted like the intent: a provider sign-up
 * leaves the page and comes back through the OAuth callback.
 */
export type TRegPaywallRequest = { at: number }

// Replay a stored intent only within this window (guards a stale localStorage intent from replaying
// on an unrelated later login — audio-web's per-tab sessionStorage isn't available in the muse engine).
export const INTENT_TTL_MS = 10 * 60 * 1000

type TAuthIntentState = {
    intent: TAuthIntent | null
    setAt: number
    regPaywall: TRegPaywallRequest | null
}

export const authIntentStore = createStoreChunk<TAuthIntentState>('authIntent')({
    persist: { localStorage: ['intent', 'setAt', 'regPaywall'] },
    initialState: { intent: null, setAt: 0, regPaywall: null },
    // Timestamps arrive in the payload: an action body runs inside the reducer, and reading the
    // clock there would make the same action on the same state produce a different result.
    actions: {
        setIntent: ({ intent, at }: { intent: TAuthIntent | null; at: number }) => ({
            intent,
            setAt: intent ? at : 0,
        }),
        clearIntent: () => ({ intent: null, setAt: 0 }),
        // Restarts the TTL when something of ours delays the replay (the paywall), so time the user
        // spends reading it is not charged against the intent.
        touchIntent: (at: number) => ({ setAt: at }),
        armRegPaywall: (at: number) => ({ regPaywall: { at } }),
        clearRegPaywall: () => ({ regPaywall: null }),
    },
})
